import { useCallback, useEffect, useMemo, useState } from "react";
import { Editor, Range, Transforms } from "slate";
import { ReactEditor } from "slate-react";
import type { EntityRefKind } from "@common/schemas/chatEntityRef.schema";

interface SlashCommand {
    id: string;
    description: string;
    entityKind?: EntityRefKind;
}

interface UseSlashCommandsParams {
    editor: Editor;
    isEditing: boolean;
    hasOrganization: boolean;
    canChangeOrganizationScope: boolean;
    setIsOrganizationScopedMessage: (value: boolean) => void;
    onCreateTask?: () => void;
}

const ENTITY_COMMANDS: SlashCommand[] = [
    {
        id: "account",
        description: "Reference a financial account",
        entityKind: "financialAccount",
    },
    {
        id: "entity",
        description: "Reference an entity",
        entityKind: "entity",
    },
    {
        id: "counterparty",
        description: "Reference a counterparty",
        entityKind: "counterparty",
    },
    {
        id: "tasks",
        description: "Reference a task",
        entityKind: "task",
    },
];

export function useSlashCommands({
    editor,
    isEditing,
    hasOrganization,
    canChangeOrganizationScope,
    setIsOrganizationScopedMessage,
    onCreateTask,
}: UseSlashCommandsParams) {
    const [slashTarget, setSlashTarget] = useState<Range | null>(null);
    const [slashSearch, setSlashSearch] = useState("");
    const [slashQuery, setSlashQuery] = useState<string | null>(null);
    const [slashIndex, setSlashIndex] = useState(0);

    const slashCommands = useMemo<SlashCommand[]>(() => {
        const commands: SlashCommand[] = [];
        if (!isEditing) {
            if (hasOrganization && canChangeOrganizationScope) {
                commands.push({
                    id: "internal",
                    description: "Only visible to your firm",
                });
                commands.push({
                    id: "external",
                    description: "Visible to everyone",
                });
            }
            if (onCreateTask) {
                commands.push({
                    id: "task",
                    description: "Create a new task from this task",
                });
            }
        }
        commands.push(...ENTITY_COMMANDS);
        return commands;
    }, [hasOrganization, isEditing, canChangeOrganizationScope, onCreateTask]);

    const filteredCommands = useMemo(() => {
        if (!slashSearch) {
            return slashCommands;
        }
        const lower = slashSearch.toLowerCase();
        return slashCommands.filter((cmd) => cmd.id.startsWith(lower));
    }, [slashCommands, slashSearch]);

    const activeEntityKind = useMemo<EntityRefKind | null>(() => {
        if (slashQuery === null) {
            return null;
        }
        const command = slashCommands.find(({ id }) => id === slashSearch);
        return command?.entityKind ?? null;
    }, [slashCommands, slashSearch, slashQuery]);

    // Clamp slashIndex when filteredCommands shrinks. The entity stage reuses
    // the index for its own options, so the commands list must not clamp it.
    useEffect(() => {
        if (activeEntityKind !== null) {
            return;
        }
        if (slashIndex >= filteredCommands.length) {
            setSlashIndex(Math.max(0, filteredCommands.length - 1));
        }
    }, [activeEntityKind, filteredCommands.length, slashIndex]);

    const resetSlashState = useCallback(() => {
        setSlashTarget(null);
        setSlashSearch("");
        setSlashQuery(null);
        setSlashIndex(0);
    }, []);

    const selectSlashCommand = useCallback(
        (commandId: string) => {
            if (!slashTarget) {
                return;
            }
            const command = slashCommands.find(({ id }) => id === commandId);
            Transforms.select(editor, slashTarget);
            if (command?.entityKind) {
                Transforms.insertText(editor, `/${command.id} `);
                return;
            }
            Transforms.delete(editor);
            let refocusEditor = true;
            switch (commandId) {
                case "internal":
                    setIsOrganizationScopedMessage(true);
                    break;
                case "external":
                    setIsOrganizationScopedMessage(false);
                    break;
                case "task":
                    onCreateTask?.();
                    refocusEditor = false;
                    break;
                default:
                    break;
            }
            resetSlashState();
            if (refocusEditor) {
                ReactEditor.focus(editor);
            }
        },
        [
            editor,
            slashTarget,
            slashCommands,
            setIsOrganizationScopedMessage,
            resetSlashState,
            onCreateTask,
        ],
    );

    return {
        slashTarget,
        setSlashTarget,
        slashSearch,
        setSlashSearch,
        slashQuery,
        setSlashQuery,
        slashIndex,
        setSlashIndex,
        slashCommands,
        filteredCommands,
        activeEntityKind,
        selectSlashCommand,
        resetSlashState,
    };
}
